import {
  Component,
  EventEmitter,
  Inject,
  Input,
  OnInit,
  Output,
} from '@angular/core';
import {
  MAT_DIALOG_DATA,
  MatDialog,
  MatDialogRef,
} from '@angular/material/dialog';
import { MessagesEnum } from 'src/app/domain/enums/messages.enum';
import { TypeIndexEnum } from 'src/app/domain/registration/enums/type-index.enum';
import { DialogConfirmComponent } from 'src/app/shared/components/dialog-confirm/dialog-confirm.component';
import { GlobalUtils } from 'src/app/shared/global-utils';
import { ToastPositionEnum } from 'src/app/shared/services/toast/toast-position.enum';
import { ToastService } from 'src/app/shared/services/toast/toast.service';
import {
  deriveControllerGroupLabel,
  PermissionEntry,
  PermissionGroupCatalogEntry,
} from 'src/app/shared/components/permission-selector/permission-selector.component';
import { FrontFirstTreeGroupDto } from 'src/app/shared/components/front-first-permission-tree/front-first-permission-tree.types';
import { PermissionGroupService } from '../permission-groups/crud-service';
import { UserPermissionService } from './crud-service';

/**
 * Permissões do Usuário (override por usuário) — migrada da mat-table ad-hoc
 * para o componente único `app-permission-selector` (spec
 * permission-selector-consolidation.md). Endpoint/gravação inalterados:
 * GET /user-permissions?user= (flat) + PUT /user-permissions/changeActives.
 *
 * `permissionCatalog` reusa o MESMO endpoint `permission-groups` já usado por
 * user-group-permissions/user-profile (frontend-only, zero mudança de
 * backend) — quando vier vazio, o componente deriva grupos sintéticos das
 * folhas via `groupHint` (convenção de controller), igual às demais telas.
 */
@Component({
  selector: 'app-user-permissions',
  templateUrl: './user-permissions.component.html',
  styleUrls: ['./user-permissions.component.sass'],
  providers: [UserPermissionService, PermissionGroupService],
})
export class UserPermissionsComponent implements OnInit {
  @Output() changePage: EventEmitter<any> = new EventEmitter<any>();
  @Input('hidePagination') hidePagination: boolean;
  @Input() hasFilter: boolean = true;
  @Input('hideFilter') hideFilter: boolean;

  /** Cache RAW da API — fonte pro payload de changeActives. */
  allPermissions: any[] = [];
  /** Catálogo macro (permission_groups existente, reaproveitado). */
  permissionCatalog: PermissionGroupCatalogEntry[] = [];
  /** Lista plana pro app-permission-selector. */
  permissionsForSelector: PermissionEntry[] = [];
  /** Seleção atual (contrato imutável). */
  selectedIds: string[] = [];
  permissionsLoading = false;
  /** Árvore "Por tela" (own-only do ator). Vazia/erro → o seletor segue só Simples/Avançado. */
  frontFirstGroups: FrontFirstTreeGroupDto[] = [];
  frontFirstLoading = false;

  applied: boolean = false;
  element: any;
  title: any = 'Permissões do Usuário';

  constructor(
    public dialog: MatDialog,
    private service: UserPermissionService,
    private permissionGroupService: PermissionGroupService,
    public dialogRef: MatDialogRef<UserPermissionsComponent>,
    private toastService: ToastService,
    @Inject(MAT_DIALOG_DATA)
    data: {
      type: TypeIndexEnum;
      select: boolean;
      element: any;
    }
  ) {
    this.element = data.element;
    const userLabel =
      this.element?.firstName ||
      this.element?.name ||
      this.element?.email ||
      '';
    this.title = userLabel ? `${this.title} - ${userLabel}` : this.title;
  }

  ngOnInit(): void {
    this.dialogRef
      .beforeClosed()
      .subscribe(() =>
        this.dialogRef.close({ element: { applied: this.applied } })
      );
  }

  ngAfterViewInit(): void {
    this.loadPermissionGroups();
    this.loadFrontFirstTree();
    this.loadList();
  }

  /** Carrega a árvore "Por tela"; qualquer falha só esconde o modo (Simples/Avançado seguem). */
  loadFrontFirstTree(): void {
    this.frontFirstLoading = true;
    this.permissionGroupService.getFrontFirstTree().subscribe({
      next: (res) => {
        this.frontFirstGroups = Array.isArray(res?.groups) ? res.groups : [];
        this.frontFirstLoading = false;
      },
      error: () => {
        this.frontFirstGroups = [];
        this.frontFirstLoading = false;
      },
    });
  }

  loadPermissionGroups(): void {
    this.permissionGroupService
      .listAll({ getRelation: 1, limit: 5000, page: 0, filter: '' })
      .subscribe(([rows]: any) => {
        const groups = Array.isArray(rows) ? rows : [];
        this.permissionCatalog = groups
          .map((group: any) => ({
            id: String(group?.id || ''),
            name: String(group?.name || ''),
            permissionIds: Array.isArray(group?.permissionIds)
              ? group.permissionIds.map((id: any) => String(id))
              : Array.isArray(group?.permissions)
                ? group.permissions
                    .map((permission: any) => String(permission?.id || ''))
                    .filter((id: string) => !!id)
                : [],
          }))
          .filter((group: PermissionGroupCatalogEntry) => !!group.id && !!group.name);
      });
  }

  public loadList(): void {
    if (!this.element?.id) {
      this.allPermissions = [];
      this.permissionsForSelector = [];
      this.selectedIds = [];
      return;
    }

    this.permissionsLoading = true;
    this.service
      .listAll({ user: this.element.id, getRelation: 1, limit: 5000, page: 0, filter: '' })
      .subscribe((dataWithPagination: any) => {
        this.allPermissions = dataWithPagination[0] || [];
        this.rebuildSelectorInputs();
        this.permissionsLoading = false;
      });
  }

  /** Deriva `permissionsForSelector`/`selectedIds` a partir do cache RAW. */
  private rebuildSelectorInputs(): void {
    this.permissionsForSelector = this.allPermissions.map((item) => {
      const actionName = this.extractActionName(item);
      return {
        id: String(item.systemActionParamId),
        name: item.name,
        groupHint: actionName ? deriveControllerGroupLabel(actionName) : undefined,
      };
    });
    this.selectedIds = this.allPermissions
      .filter((item) => !!item.allow)
      .map((item) => String(item.systemActionParamId));
  }

  /** Nome técnico (Controller_metodo), só se a resposta já trouxer a relação. */
  private extractActionName(item: any): string | undefined {
    return item?.sa_name || item?.systemAction?.name || item?.actionName || undefined;
  }

  onSelectedIdsChange(ids: string[]): void {
    this.selectedIds = ids;
  }

  public hasValue(): boolean {
    return (this.selectedIds?.length || 0) > 0;
  }

  public confirm() {
    GlobalUtils.openDialog({
      dialog: this.dialog,
      data: { title: 'CONFIRMAÇÃO' },
      component: DialogConfirmComponent,
      callBack: (data: any) => {
        if (!data?.result) return;

        const selected = new Set(this.selectedIds.map(String));
        this.service
          .changeActives(
            this.allPermissions.map((item) => ({
              user: this.element.id,
              systemActionParam: item.systemActionParamId,
              allow: selected.has(String(item.systemActionParamId)),
            }))
          )
          .subscribe((item) => {
            this.toastService.showSuccess(MessagesEnum.SUCCESS_OPERATION, {
              position: ToastPositionEnum.BOTTOM_END,
              toast: true,
              timer: 3000,
              showConfirmButton: false,
              backdrop: '',
            });
            this.applied = true;
          });
      },
    });
  }
}
